import client from '@/api/client';

import crudlModuleGenerator from '@/_CRUDL/modules/generator';

export const COMMISSIONS_UPDATE_CLEAR = 'proposal_attempts/commissions_update/CLEAR';
export const COMMISSIONS_UPDATE_START = 'proposal_attempts/commissions_update/START';
export const COMMISSIONS_UPDATE_SUCCESS = 'proposal_attempts/commissions_update/SUCCESS';
export const COMMISSIONS_UPDATE_FAILURE = 'proposal_attempts/commissions_update/FAILURE';

const multiple = '/proposals/:proposal_id/proposal_attempts';
const single = '/proposals/:proposal_id/proposal_attempts/:id/:status?';

const proposalAttempts = crudlModuleGenerator('proposalAttempts', {
  customEndpoints: {
    create: multiple,
    read: single,
    update: single,
    delete: single,
    list: multiple,
  },
});

// O START da list do _CRUDL zera `list.items` ANTES do request: `handle(state)` é
// chamado sem resposta, `data` fica `{}` e a última linha dele atribui isso ao
// estado (src/_CRUDL/generators/mutations.js). Fazia sentido quando a lista só
// era refeita por troca de filtro, em que mostrar linha velha sob filtro novo é
// pior que mostrar nada.
//
// Aqui é o contrário: a tela da proposta refaz a busca a cada evento do Pusher, e
// o bloco de informações é montado por `v-if="Object.keys(attempts).length"` — a
// lista esvaziando desmontaria e remontaria a tela inteira a cada atualização de
// status, com uma ida ao servidor em branco no meio. A lista fica de pé até a
// resposta chegar, e `list.loading` continua sendo a fonte para quem quiser
// indicar carregamento.
//
// O que a lista NÃO faz mais sozinha, a tela faz de propósito: `Proposals/Read.vue`
// limpa ao entrar, para não herdar as tentativas da proposta anterior.
//
// Vale para os DOIS lados do request. O FAILURE zera igual ao START, e uma falha
// passageira do refetch disparado pelo Pusher é justamente quando a lista mais
// precisa ficar de pé: apagá-la desmontaria o bloco inteiro pelo caminho infeliz,
// que é o sintoma que esta sobrescrita existe para evitar.
//
// A sobrescrita é DESTE módulo, não do _CRUDL: `proposalAttempts/list` é
// despachado num lugar só, `packages/_SDK/src/views/Proposals/Read.vue`.
/* eslint-disable no-underscore-dangle */
const LIST_START = proposalAttempts._crudl.constants.list.start;
const LIST_FAILURE = proposalAttempts._crudl.constants.list.failure;
/* eslint-enable no-underscore-dangle */

// Delegar e devolver os `items` em vez de reescrever a mutação: o resto do que
// ela faz (datetime, loading, e o formato de erro que o _CRUDL sabe desembrulhar)
// continua sendo dele. Reimplementar aqui deixaria duas cópias da leitura de erro,
// e a daqui pararia no tempo.
const listStart = proposalAttempts.mutations[LIST_START];
const listFailure = proposalAttempts.mutations[LIST_FAILURE];

function keepingItems(mutation) {
  return function keepItems(state, payload) {
    const { items } = state.list;

    mutation(state, payload);

    state.list.items = items;
  };
}

function identify(response, id) {
  // eslint-disable-next-line no-underscore-dangle, no-param-reassign
  response._INTERNAL = {
    id,
  };
}

export default {
  ...proposalAttempts,
  state: {
    ...proposalAttempts.state,

    commissionsUpdate: {
      loading: false,
      result: {},
      error: null,
    },
  },

  actions: {
    ...proposalAttempts.actions,

    commissionsUpdate({ commit }, { proposal_id: proposalID, id, payload }) {
      commit(COMMISSIONS_UPDATE_START);
      return new Promise((resolve, reject) => {
        client.put(`/proposals/${proposalID}/proposal_attempts/${id}/bank_commissions`, payload)
          .then((response) => {
            identify(response, id);

            commit(COMMISSIONS_UPDATE_SUCCESS, response);
            resolve(response);
          })
          .catch((error) => {
            commit(COMMISSIONS_UPDATE_FAILURE, error);
            reject(error);
          });
      });
    },

    commissionsUpdateClear({ commit }) {
      return new Promise((resolve) => {
        commit(COMMISSIONS_UPDATE_CLEAR);
        resolve();
      });
    },
  },

  mutations: {
    ...proposalAttempts.mutations,

    // Sobrescreve o START e o FAILURE da list: tudo igual ao _CRUDL, menos o
    // zeramento de `items`. O porquê está no comentário de LIST_START, acima.
    [LIST_START]: keepingItems(listStart),
    [LIST_FAILURE]: keepingItems(listFailure),

    [COMMISSIONS_UPDATE_CLEAR](state) {
      state.commissionsUpdate.error = null;
      state.commissionsUpdate.result = {};
      state.commissionsUpdate.loading = false;
    },
    [COMMISSIONS_UPDATE_START](state) {
      state.commissionsUpdate.error = null;
      state.commissionsUpdate.result = {};
      state.commissionsUpdate.loading = true;
    },
    [COMMISSIONS_UPDATE_SUCCESS](state, response) {
      // eslint-disable-next-line no-underscore-dangle
      const id = response._INTERNAL && response._INTERNAL.id;

      if (id) {
        state.list.items[id].bank_commissions = response.data.bank_commissions;
      }

      state.commissionsUpdate.error = null;
      state.commissionsUpdate.result = response.data;
      state.commissionsUpdate.loading = false;
    },
    [COMMISSIONS_UPDATE_FAILURE](state, error) {
      let details = error;

      try {
        details = error.response.data.error;
      } catch (err) { /* */ }

      state.commissionsUpdate.error = details;
      state.commissionsUpdate.result = {};
      state.commissionsUpdate.loading = false;
    },
  },
};
